import React from 'react';
import styled from 'styled-components';
import { businessStayTexts } from '../config/businessStayTexts';
import { useLanguage } from '../context/LanguageContext';
import { hotelInfo } from '../config/hotelTexts';

const PageHeader = styled.div`
  height: 40vh;
  min-height: 300px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url('/images/etapes.jpg');
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  margin-top: 70px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  text-align: center;
  text-shadow: 4px 4px 12px rgba(0, 0, 0, 1), 2px 2px 8px rgba(0, 0, 0, 0.9);
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 700;
  letter-spacing: 1px;
`;

const PageSubtitle = styled.p`
  font-size: 1.3rem;
  text-align: center;
  text-shadow: 3px 3px 10px rgba(0, 0, 0, 1), 1px 1px 6px rgba(0, 0, 0, 0.9);
  color: ${({ theme }) => theme.colors.white};
  font-weight: 500;
`;

const IntroSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.white};
`;

const IntroContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const IntroTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const IntroDescription = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const FormulaBox = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const FormulaTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const FormulaList = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const FormulaItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.accent};
    flex-shrink: 0;
  }
`;

const PackagesSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.light};
`;

const PackagesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const PackageCard = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  overflow: hidden;
  transition: transform ${({ theme }) => theme.transitions.medium}, box-shadow ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.large};
  }
`;

const RoomImageTop = styled.div`
  height: 250px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const PackageTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`;

const PackageRoom = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-weight: 500;
`;

const PackageContent = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
`;

const PackageDescription = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;


const PackagePrice = styled.div`
  text-align: center;
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 2px solid ${({ theme }) => theme.colors.light};
`;

const Price = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const PriceLabel = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

const AdvantagesSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.white};
`;

const AdvantagesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const AdvantagesTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
`;

const AdvantagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
`;

const AdvantageCard = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const AdvantageIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.5rem;
`;

const AdvantageTitle = styled.h3`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const AdvantageDescription = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
`;

const BookingSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.light};
`;

const BookingContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const BookingTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const BookingDescription = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const ContactInfo = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ContactItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ContactLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const ContactValue = styled.a`
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  font-size: 1.1rem;
  
  &:hover {
    text-decoration: underline;
  }
`;


const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
  </svg>
);

const BusinessStay = () => {
  const { language } = useLanguage();
  const texts = businessStayTexts[language] || businessStayTexts.fr;
  
  return (
    <>
      <PageHeader>
        <PageTitle>{texts.pageTitle}</PageTitle>
        <PageSubtitle>{texts.subtitle}</PageSubtitle>
      </PageHeader>
      
      <IntroSection>
        <IntroContainer>
          <IntroTitle>{texts.intro.title}</IntroTitle>
          <IntroDescription>{texts.intro.description}</IntroDescription>
          
          <FormulaBox>
            <FormulaTitle>{texts.formula.title}</FormulaTitle>
            <FormulaList>
              {texts.formula.items.map((item, index) => (
                <FormulaItem key={index}>
                  <CheckIcon />
                  {item}
                </FormulaItem>
              ))}
            </FormulaList>
          </FormulaBox>
        </IntroContainer>
      </IntroSection>
      
      <PackagesSection>
        <PackagesContainer>
          {texts.packages.map((pkg) => {
            const roomImage = pkg.id === 'traditional' ? '/images/simple.jpg' : '/images/design.jpg';
            return (
              <PackageCard key={pkg.id}>
                <RoomImageTop image={roomImage} />
                <PackageContent>
                  <PackageTitle>{pkg.title}</PackageTitle>
                  <PackageRoom>{pkg.room}</PackageRoom>
                  <PackageDescription>{pkg.description}</PackageDescription>
                  
                  <PackagePrice>
                    <Price>{pkg.price}€</Price>
                    <PriceLabel>{texts.priceInfo}</PriceLabel>
                  </PackagePrice>
                </PackageContent>
              </PackageCard>
            );
          })}
        </PackagesContainer>
      </PackagesSection>
      
      <AdvantagesSection>
        <AdvantagesContainer>
          <AdvantagesTitle>{texts.advantages.title}</AdvantagesTitle>
          <AdvantagesGrid>
            {texts.advantages.items.map((advantage, index) => (
              <AdvantageCard key={index}>
                <AdvantageIcon>✓</AdvantageIcon>
                <AdvantageTitle>{advantage.title}</AdvantageTitle>
                <AdvantageDescription>{advantage.description}</AdvantageDescription>
              </AdvantageCard>
            ))}
          </AdvantagesGrid>
        </AdvantagesContainer>
      </AdvantagesSection>
      
      <BookingSection>
        <BookingContainer>
          <BookingTitle>{texts.booking.title}</BookingTitle>
          <BookingDescription>{texts.booking.description}</BookingDescription>
          
          <ContactInfo>
            <ContactItem>
              <ContactLabel>{texts.booking.phoneLabel}</ContactLabel>
              <ContactValue href={`tel:${hotelInfo.contact.phone}`}>
                {hotelInfo.contact.phone}
              </ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>{texts.booking.emailLabel}</ContactLabel>
              <ContactValue href={`mailto:${hotelInfo.contact.email}`} target="_blank" rel="noopener noreferrer">
                {hotelInfo.contact.email}
              </ContactValue>
            </ContactItem>
          </ContactInfo>
          
        </BookingContainer>
      </BookingSection>
    </>
  );
};

export default BusinessStay;
